import { slugifySpecialty } from "./specialty-slug";
import { isThinSpecialty } from "./structured-data";

/**
 * Location pages, generated from real listings only:
 *   /directory/area/:areaSlug              — every specialist with rooms in a suburb
 *   /directory/:specialtySlug/:areaSlug    — e.g. "Haematologists in Garran"
 *
 * Both use the thin-page rule (fewer than 3 listings → not generated,
 * not in the sitemap, noindex if reached in the SPA), so new pages appear
 * automatically as listings grow and none are near-empty doorways.
 * Shared by the SPA and scripts/prerender.mjs.
 */

/** Canberra district each suburb sits in — how many patients search ("specialist Woden"). */
const ACT_DISTRICTS: Record<string, string> = {
  garran: "Woden Valley",
  phillip: "Woden Valley",
  woden: "Woden Valley",
  deakin: "Inner South",
  barton: "Inner South",
  kingston: "Inner South",
  griffith: "Inner South",
  forrest: "Inner South",
  bruce: "Belconnen",
  belconnen: "Belconnen",
  canberra: "Canberra City",
  "canberra-city": "Canberra City",
  acton: "Canberra City",
  braddon: "Inner North",
  turner: "Inner North",
  greenway: "Tuggeranong",
  tuggeranong: "Tuggeranong",
  gungahlin: "Gungahlin",
};

export interface LocalListing {
  slug: string;
  name: string;
  specialty: string | null | undefined;
  suburb: string | null | undefined;
  practice?: string | null;
}

export interface AreaSummary {
  slug: string;
  /** Display name, e.g. "Garran" ("Canberra City" for the 2601 "Canberra" locality). */
  name: string;
  district: string | null;
  count: number;
  specialties: Array<{ slug: string; name: string; count: number }>;
}

export function areaSlug(suburb: string): string {
  return slugifySpecialty(suburb);
}

export function areaDisplayName(suburb: string): string {
  const trimmed = suburb.trim();
  return trimmed.toLowerCase() === "canberra" ? "Canberra City" : trimmed;
}

export function areaDistrict(slug: string): string | null {
  return ACT_DISTRICTS[slug] ?? null;
}

/** "Garran (Woden Valley)" — or just the name when the district adds nothing. */
export function areaLabel(area: Pick<AreaSummary, "name" | "district">): string {
  return area.district && area.district !== area.name ? `${area.name} (${area.district})` : area.name;
}

/** All suburbs with listings, busiest first, each with its specialty breakdown. */
export function buildAreas(listings: LocalListing[]): AreaSummary[] {
  const areas = new Map<string, AreaSummary>();
  for (const listing of listings) {
    const suburb = listing.suburb?.trim();
    if (!suburb) continue;
    const slug = areaSlug(suburb);
    if (!slug) continue;
    const area =
      areas.get(slug) ??
      { slug, name: areaDisplayName(suburb), district: areaDistrict(slug), count: 0, specialties: [] };
    area.count += 1;
    const specialtyName = listing.specialty?.trim();
    if (specialtyName) {
      const specialtySlug = slugifySpecialty(specialtyName);
      const entry = area.specialties.find((item) => item.slug === specialtySlug);
      if (entry) entry.count += 1;
      else area.specialties.push({ slug: specialtySlug, name: specialtyName, count: 1 });
    }
    areas.set(slug, area);
  }
  return [...areas.values()]
    .map((area) => ({
      ...area,
      specialties: area.specialties.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

/** Areas with enough listings to deserve an indexable page. */
export function indexableAreas(listings: LocalListing[]): AreaSummary[] {
  return buildAreas(listings).filter((area) => !isThinSpecialty(area.count));
}

/** Specialty × area combinations with enough listings for their own page. */
export function indexableSpecialtyAreas(
  listings: LocalListing[],
): Array<{ area: AreaSummary; specialty: { slug: string; name: string; count: number } }> {
  return buildAreas(listings).flatMap((area) =>
    area.specialties
      .filter((specialty) => !isThinSpecialty(specialty.count))
      .map((specialty) => ({ area, specialty })),
  );
}

export function listingsInArea(listings: LocalListing[], slug: string, specialtySlug?: string): LocalListing[] {
  return listings.filter(
    (listing) =>
      listing.suburb?.trim() &&
      areaSlug(listing.suburb) === slug &&
      (!specialtySlug || slugifySpecialty(listing.specialty) === specialtySlug),
  );
}
